import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from '../../../utilities/axios';
import * as API_END_POINT from '../../../utilities/constant';

import SideFilter from '../../../components/Common/SideFilter';
import FilterPills from '../../../components/Common/Dropdowns/FilterPills';

import {
    Browse,
    ProductCard,
    GridSkeleton,
    Skeleton,
    ShareMeta,
    Pager,
    collectionImageOf,
} from '../../../shared/components';
import { useStore } from '../../../context/StoreContext';
import { useStorefrontTheme } from '../../../shared/theme/storefrontTheme';
import '../../../shared/components/organisms/BrandHeader/brandHeader.css';

/* COLLECTION DETAIL — `/collections/:id`.

   Ported from `BrandDetails`, this codebase's pattern for a single-entity
   storefront page: crumb → identity header → `Browse`, no tabs, no photo hero,
   a pager under the grid. The older implementation (`CustomDropdown`, `PageLayout`,
   `components/Common/Card/ProductCard`, `PaginationCommon`) predated the shared
   component layer and is gone.

   The reference page for this route does not exist: SP-main links to
   `shop-collection.html?id=` but that file was never built (same gap as
   `shop-brand.html`). What it has is `shop-collections.html` and `SF.collRow` —
   the SUMMARY level, ported as the `CollectionRow` organism for the index. This
   file is the DETAIL level, and the identity header borrows `BrandHead`'s `.sb-*`
   CSS rather than the component, whose markup always renders an image mark —
   right for a brand's logo, wrong for a collection. `.sb-head` is
   `display:flex; gap:18px`, so a single child lays out the same.

   No collection-scoped filters endpoint exists, so — as the old version already
   did — the rail is built from the store-wide `PRODUCT_FILTERS`. */
const CollectionProducts = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const [manualPriceRange, setManualPriceRange] = useState({ min: '', max: '' });
    const [collectionInfo, setCollectionInfo] = useState({});
    const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 10 });
    const [products, setProducts] = useState([]);
    const [filtersView, setfiltersView] = useState([]);
    const [filters, setFilters] = useState({
        vendorValues: [],
        brandValues: [],
        valueSearch: '',
        priceRange: [],
        animal_type: [],
        size: [],
        life_stage: [],
        tags: [],
        categories: [],
        discounts: []
    });
    const [sortBy, setSortBy] = useState('Our order');
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [limit] = useState(100);
    /* The page belongs to the filters and sort it was turned on: changing
       either starts again at page 1. Derived rather than reset in an effect, so
       the change fetches once — same pattern as `BrandDetails`. */
    const queryKey = `${JSON.stringify(filters)}|${sortBy}`;
    const [pageOf, setPageOf] = useState({ key: queryKey, page: 1 });
    const currentPage = pageOf.key === queryKey ? pageOf.page : 1;
    const offset = (currentPage - 1) * limit;
    const handlePageChange = (page) => {
        setPageOf({ key: queryKey, page });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };
    // The collection's own order first, then the storefront sort list (no Recommended: the order is the recommendation).
    const sortOptions = [
        'Our order',
        'Product (A–Z)',
        'Product (Z–A)',
        'Brand (A–Z)',
        'Brand (Z–A)',
        'Price (low to high)',
        'Price (high to low)',
    ];

    const cancelReq = useRef(null);
    const latestReqRef = useRef(0);
    const fetchProductList = async () => {
        setIsLoading(true);
        latestReqRef.current += 1;
        const requestId = latestReqRef.current;

        if (cancelReq?.current) {
            cancelReq?.current?.cancel('Cancelled due to new request');
        }
        const source = axios.CancelToken.source();
        cancelReq.current = source;

        try {
            const filterArr = [];
            const searchKeyword = searchTerm;
            if (filters.priceRange && filters.priceRange.length > 0) {
                filters.priceRange.map((value) => {
                    filterArr.push({
                        field: 'pricerange',
                        options: { min: value.min, max: value.max }
                    });
                });
            }
            if (filters.brandValues && filters.brandValues.length > 0) {
                filterArr.push({
                    field: 'brands',
                    options: { operator: 'in', values: filters.brandValues.map((e) => e.toString()) }
                });
            }
            if (filters.vendorValues && filters.vendorValues.length > 0) {
                filterArr.push({
                    field: 'vendors',
                    options: { operator: 'in', values: filters.vendorValues.map((e) => e.toString()) }
                });
            }
            if (filters.categories && filters.categories.length > 0) {
                let subcategory = [];
                filters.categories.map((category) => {
                    if (category?.subcategory?.length > 0) subcategory.push(...category.subcategory);
                });
                filterArr.push({
                    field: 'subcategory',
                    options: { operator: 'in', values: subcategory.map((e) => e.toString()) }
                });
            }
            if (filters.tags && filters.tags.length > 0) {
                filterArr.push({
                    field: 'tags',
                    options: { operator: 'in', values: filters.tags.map((e) => e.toString()) }
                });
            }

            let sortArr = [];
            if (sortBy === 'Product (A–Z)') sortArr.push({ fieldName: 'product_name', fieldOrder: 'ASC' });
            else if (sortBy === 'Product (Z–A)') sortArr.push({ fieldName: 'product_name', fieldOrder: 'DESC' });
            else if (sortBy === 'Brand (A–Z)') sortArr.push({ fieldName: 'brand', fieldOrder: 'ASC' });
            else if (sortBy === 'Brand (Z–A)') sortArr.push({ fieldName: 'brand', fieldOrder: 'DESC' });
            else if (sortBy === 'Price (low to high)') sortArr.push({ fieldName: 'price_msrp', fieldOrder: 'ASC' });
            else if (sortBy === 'Price (high to low)') sortArr.push({ fieldName: 'price_msrp', fieldOrder: 'DESC' });
            else sortArr.push({ fieldName: 'sequence', fieldOrder: 'ASC' });

            const response = await axios.post(
                `${API_END_POINT.FETCH_COLLECTION(id)}`,
                {
                    paging: { limit, offset },
                    sort: sortArr,
                    ...(filterArr.length ? { filter: filterArr } : null),
                    ...(searchKeyword ? { query: { searchKeyword } } : {})
                },
                { cancelToken: source.token }
            );

            const rows = response.data.data.products || [];

            if (requestId !== latestReqRef.current) return;

            setProducts(rows);
            setCollectionInfo(response.data.data.collection);
            setPagination(response.data.data.pagination);
        } catch (error) {
            console.error('Error fetching collection:', error);
        } finally {
            if (requestId === latestReqRef.current) setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchProductList();
    }, [id]);

    useEffect(() => {
        fetchProductList();
    }, [filters, sortBy, offset]);

    useEffect(() => {
        getAllProductsFilter();
    }, []);
    const [productFilters, setProductFilters] = useState(null);
    // A collection's rail is Category · Brand · Price range, as in the reference — no store-wide tag groups.
    const collectionFilters = useMemo(
        () => productFilters && { ...productFilters, tagGroups: [], brandValues: undefined, hasSample: undefined },
        [productFilters]
    );
    const getAllProductsFilter = async () => {
        const response = await axios.post(`${API_END_POINT.PRODUCT_FILTERS}`, {});
        setProductFilters(response.data.data?.availableFilters);
    };

    const clearFilters = () => {
        setManualPriceRange({ min: '', max: '' });
        setFilters({
            vendorValues: [],
            brandValues: [],
            valueSearch: '',
            priceRange: [],
            animal_type: [],
            size: [],
            life_stage: [],
            tags: [],
            categories: [],
            discounts: []
        });
        setSearchParams({});
    };
    useEffect(() => {
        if (!searchParams.get('category') && !searchParams.get('brands') && !searchParams.get('vendors') && !searchParams.get('brand_values') && !searchParams.get('price') && !searchParams.get('tags')) {
            clearFilters();
            setfiltersView([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setSearchParams]);

    /* The partner owns the skin, same mechanism as `BrandDetails`: `--o` and
       `--m` are written onto this subtree from the saved Style config. */
    const { pageDetails } = useStore();
    const sfxRef = useStorefrontTheme(pageDetails?.page_data?.colorAndApperances);

    const isBusy = isLoading || !productFilters;
    const collectionName = collectionInfo?.name;
    const totalItems = pagination?.totalItems ?? pagination?.total;

    const crumb = [
        { label: 'Home', href: '/' },
        { label: 'Collections', href: '/all-collections' },
        { label: collectionName || '' },
    ];

    const appliedCount = filtersView?.length || 0;

    return (
        <div ref={sfxRef} className="sfx">
            <ShareMeta
                title={collectionName}
                description={collectionInfo?.description}
                image={collectionImageOf([{ ...collectionInfo, products }])}
            />

            <div className="bd-wrap">
                <nav className="br-crumb" aria-label="Breadcrumb">
                    {crumb.map((c, i) => (
                        <React.Fragment key={`${c.label}-${i}`}>
                            {i > 0 && ' / '}
                            {c.href ? <Link to={c.href}>{c.label}</Link> : <span>{c.label}</span>}
                        </React.Fragment>
                    ))}
                </nav>

                {collectionName ? (
                    <header className="sb-head">
                        <div className="sb-id">
                            <h1 className="sb-name">{collectionName}</h1>
                            {collectionInfo?.description && <p className="sb-line">{collectionInfo.description}</p>}
                            {typeof totalItems === 'number' && (
                                <p className="sb-facts">
                                    <span>{totalItems} product{totalItems === 1 ? '' : 's'}</span>
                                </p>
                            )}
                        </div>
                    </header>
                ) : (
                    <header className="sb-head" aria-hidden="true">
                        <div className="sb-id">
                            <Skeleton w={220} h={34} />
                            <div style={{ marginTop: 8 }}><Skeleton w={280} h={16} /></div>
                        </div>
                    </header>
                )}
            </div>

            <Browse
                noun="products"
                total={totalItems}
                items={isBusy ? [] : products}
                loading={isBusy}
                loadingView={<GridSkeleton count={9} />}
                searchValue={searchTerm}
                searchPlaceholder="Search this collection"
                onSearchChange={(val) => {
                    setSearchTerm(val);
                    setPageOf({ key: queryKey, page: 1 });
                }}
                sorts={sortOptions.map((o) => [o, o])}
                sortValue={sortBy}
                onSortChange={setSortBy}
                chipsSlot={
                    appliedCount > 0 ? (
                        <FilterPills
                            filterData={productFilters}
                            setManualPriceRange={setManualPriceRange}
                            clearFilters={clearFilters}
                            filtersView={filtersView}
                            setfiltersView={setfiltersView}
                            setFilters={setFilters}
                        />
                    ) : null
                }
                chipCount={appliedCount}
                rail={
                    <SideFilter
                        setManualPriceRange={setManualPriceRange}
                        manualPriceRange={manualPriceRange}
                        filterData={collectionFilters}
                        filtersView={filtersView}
                        setfiltersView={setfiltersView}
                        filters={filters}
                        setFilters={setFilters}
                    />
                }
                belowGrid={
                    isBusy ? null : (
                        <Pager
                            page={currentPage}
                            perPage={limit}
                            total={totalItems}
                            onPageChange={handlePageChange}
                        />
                    )
                }
                emptyTitle="Nothing matches those filters"
                emptyBody="Try removing one, or clear them all to see everything in this collection."
                onClearAll={clearFilters}
                renderItem={(product) => {
                    const images = Array.isArray(product.product_images) ? product.product_images : [];
                    const main = [...images].sort((a, b) => Number(b.is_main) - Number(a.is_main))[0];
                    const brand = product?.is_multi_brand
                        ? product.vendor?.name || product.shopify_vendor?.shopdot_vendor?.display_name
                        : product.brand?.name || product.brand_name;

                    return (
                        <ProductCard
                            name={product.title}
                            brand={brand}
                            price={product.price_msrp}
                            image={main?.src}
                            href={`/product/${product.id}`}
                            onClick={(e) => {
                                if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
                                e.preventDefault();
                                navigate(`/product/${product.id}`);
                            }}
                            objectFit="contain"
                        />
                    );
                }}
            />
        </div>
    );
};

export default CollectionProducts;
